<template>
  <div class="profile-wrapper">
    <div class="spinner-container" v-if="isLoading">
      <base-spinner :is-loading="true" :is-local="true" class="spinner" />
    </div>
    <Lender
      v-if="!isLoading && profile.role === 'lender'"
      @createOffer="getLenderOffersList()"
      @deleteOffer="deleteOffer"
    />
    <Borrower
      v-if="!isLoading && profile.role === 'borrower'"
      @successPayment="updateLoansList()"
    />
    <base-modal
      v-if="isActivationModalVisible"
      @close="closeActivationModal()"
    >
      <template #main>
        <div class="activation-modal">
          <div class="modal-header">
            <img
              @click.stop="closeActivationModal()"
              class="close-image"
              src="/custom/images/cross.svg"
              alt="Cross image"
            />
          </div>
          <img
            class="activation-modal-loader"
            src="/custom/images/modal-loader.svg"
            alt="Loader"
          />
          <div class="activation-modal-title">
            {{ $t('message.profile.activationModal.title') }}
          </div>
          <div class="activation-modal-text">
            {{ $t('message.profile.activationModal.text') }}
          </div>
          <div class="activation-modal-note">
            {{ $t('message.profile.activationModal.note') }}
          </div>
          <div class="activation-modal-btn-container">
            <base-button
              :text="$t('message.profile.activationModal.btn')"
              :min-height="'48px'"
              @click="closeActivationModal()"
            />
          </div>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BaseSpinner from "../components/base/BaseSpinner.vue";
import BaseButton from "../components/base/BaseButton.vue";
import BaseModal from "../components/base/BaseModal.vue";
import Lender from "./Profile/Lender.vue";
import Borrower from "./Profile/Borrower.vue";

export default {
  name: "Profile",
  inject: [
    '$httpService'
  ],
  components: {
    BaseSpinner,
    BaseButton,
    BaseModal,
    Lender,
    Borrower
  },
  async created() {
    await this.$store.dispatch('updateProfile');
    const { loansParams, loansFilter } = this.$route.params || {};

    await this.$store.dispatch("getLoansList", {
      ...(loansParams || this.params),
      filter: loansFilter
    });
    
    if (this.profile.role === 'lender') {
      await this.$store.dispatch('getLenderOffersList', {...this.params});
    }

    this.isLoading = false;
    this.isActivationModalVisible = this.profile?.status === 'waiting';
  },
  data() {
    return {
      isLoading: true,
      isActivationModalVisible: false,
      params: {
        page: 0,
        size: 10
      }
    };
  },
  methods: {
    closeActivationModal() {
      this.isActivationModalVisible = false;
    },
    async deleteOffer(publicId) {
      this.isLoading = true;

      try {
        await this.$store.dispatch('deleteOffer', publicId);
        await this.$store.dispatch('getLenderOffersList', {...this.params});
      } finally {
        this.isLoading = false;
      }
    },
    async getLenderOffersList() {
      this.isLoading = true;
      await this.$store.dispatch('getLenderOffersList', {size: 10, page: 0});
      this.isLoading = false;
    },
    async updateLoansList() {
      this.isLoading = true;
      await this.$store.dispatch("getLoansList", {...this.params});
      this.isLoading = false;
    }
  },
  computed: {
    ...mapGetters(["profile"])
  },
};
</script>

<style lang="scss" scoped>
.profile-wrapper {
  @include page-wrapper-with-header-and-footer;

  padding-top: 20px;
  box-sizing: border-box;
  background-color: var(--profile-page-bgc);
}

.profile-container {
  @include base-page-container;

  color: #121212;
}

.spinner-container {
  @include base-page-container;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 10%;
  color: var(--profile-text-color);
}

.company-info {
  display: flex;
}

.company-name {
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 26px;
  line-height: 32px;
  display: flex;
  align-items: center;
  letter-spacing: 0.01em;
  color: #000000;
}

.rate-block {
  margin-left: 25px;
  display: flex;
  align-items: center;

  span {
    margin-top: 3px;
    margin-left: 4px;
    margin-right: 5px;
    font-weight: 400;
    font-size: 16px;
    line-height: 135%;
    color: var(--profile-rate-text-color);
  }
}

.rate {
  font-weight: 600;
  font-size: 24px;
  line-height: 130%;
  color: #41BF7B;
}

.spinner ::v-deep {
  width: 100%;
}

.modal-header {
  @include modal-close-block;

  width: 100%;
}

.close-image {
  cursor: pointer;
}

.activation-modal {
  width: 480px;
  max-width: calc(95vw - 30px);
  padding: 0 0px 24px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.activation-modal-loader {
  width: 96px;
  height: 96px;
  margin-bottom: 16px;
}

.activation-modal-title {
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.1px;
  color: #022131;
}

.activation-modal-text {
  width: 440px;
  max-width: 100%;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 22px;
  color: #022131;
  white-space: pre-line;
}

.activation-modal-note {
  width: 440px;
  max-width: 100%;
  margin-bottom: 40px;
  font-size: 12px;
  line-height: 18px;
  color: #6b6b6b;
  white-space: pre-line;
}

.activation-modal-btn-container {
  width: 440px;
  max-width: 100%;
  height: 48px;
}

@media screen and (max-width: 968px) {
  .activation-modal-loader {
    width: 80px;
    height: 80px;
  }

  .activation-modal-text {
    margin-bottom: 12px;
  }

  .activation-modal-note {
    margin-bottom: 32px;
  }
}
</style>
